import { makeObservable, observable, runInAction, computed } from 'mobx';
import { debounce } from 'lodash';
import { ContractsIngestionRuntimeInputDto } from '../../types';
import { InputBaseModel, NodeBaseModel } from '..';
import { TypeUtils } from '../../../common/misc';
import { SourceModelBase, StaticSourceModel } from '../../../custom_shared/models';
import { SourceProvider } from '../../../custom_shared/misc';
import { InputIds, InstanceUtils } from '../../misc';

interface FetchSourceOptionsParams {
    ignoreCache?: boolean;
}

export default class InputModelWithSource extends InputBaseModel {
    sourceProvider: SourceProvider;
    useCachedSourceOptions: boolean = false;

    // Ensure smooth experience by waiting for a minimum loading time
    private loadingStartTime: number | null = null;
    private loadingTimeoutId: ReturnType<typeof setTimeout> | null = null;

    private readonly minimumLoadingTime = 500;
    private readonly debounceTime = 300;

    private fetchSourceOptionsWithChildrenDebounced: () => Promise<void>;

    constructor(node: NodeBaseModel, data: ContractsIngestionRuntimeInputDto, source?: SourceModelBase) {
        super(node, data);

        this.createSourceProvider(source);
        this.fetchSourceOptionsWithChildrenDebounced = debounce(
            this.fetchSourceOptionsWithChildren.bind(this),
            this.debounceTime
        );

        makeObservable(this, {
            sourceProvider: observable,
            useCachedSourceOptions: observable,
            isLoading: computed,
            isParentLoading: computed
        });
    }

    fetchSourceOptionsHierarchy(): void {
        // Smooth experience by immediately setting loading to true
        if (this.sourceProvider.isLinkedSource && !this.sourceProvider.loading) {
            this.sourceProvider.setLoading(true);
        }

        this.fetchSourceOptionsWithChildrenDebounced();
    }

    get isLoading(): boolean {
        return this.sourceProvider.loading || this.isParentLoading;
    }

    get isParentLoading(): boolean {
        return this.parentInputs.filter(InstanceUtils.isInputWithSource).some(parentInput => parentInput.isLoading);
    }

    get formattedValue(): unknown {
        if (TypeUtils.isStringArray(this.value)) {
            return this.value.map(value => this.sourceProvider.options.find(o => o.value === value)?.label ?? value);
        }

        return this.sourceProvider.options.find(o => o.value === this.value)?.label ?? this.value;
    }

    async fetchSourceOptions({ ignoreCache }: FetchSourceOptionsParams = {}) {
        try {
            this.startLoading();

            if (this.parentInputs.length > 0) {
                await this.sourceProvider.getSourceOptionsByParent(
                    this.parentInputs.flatMap(parentInput => this.toStringArray(parentInput.value)),
                    this.parentInputValuesById
                );
            } else {
                await this.sourceProvider.getSourceOptions(ignoreCache);
            }

            this.syncValueWithOptions();

            if (this.sourceProvider.isLinkedSource) {
                runInAction(() => {
                    this.useCachedSourceOptions = true;
                });
            }
        } catch (e) {
            console.error(`Error while fetching source options for input '${this.id} ${this.guid}'.`, e);
        } finally {
            await this.stopLoading();
        }
    }

    async fetchSourceOptionsWithChildren(params: FetchSourceOptionsParams = {}): Promise<void> {
        try {
            await this.fetchSourceOptions(params);

            await Promise.all(
                this.childInputs
                    .filter(InstanceUtils.isInputWithSource)
                    .map(childInput => childInput.fetchSourceOptionsWithChildren(params))
            );
        } catch (e) {
            console.error(`Error while fetching source options for input '${this.id} ${this.guid}'.`, e);
        }
    }

    private startLoading(): void {
        if (this.sourceProvider.isLinkedSource) {
            this.cleanupLoadingTimeout();
            this.loadingStartTime = Date.now();
            this.sourceProvider.setLoading(true);
        }
    }

    private get parentInputValuesById(): Record<string, string[]> {
        return this.parentInputs.reduce(
            (valuesById, parentInput) => {
                valuesById[parentInput.id] = this.toStringArray(parentInput.value);
                return valuesById;
            },
            {} as Record<string, string[]>
        );
    }

    private toStringArray(value: unknown): string[] {
        if (TypeUtils.isString(value)) {
            return value.length > 0 ? [value] : [];
        }

        if (TypeUtils.isStringArray(value)) {
            return value;
        }

        return [];
    }

    private async stopLoading(): Promise<void> {
        if (this.sourceProvider.isLinkedSource) {
            await this.ensureMinimumLoadingTime();
            this.loadingStartTime = null;
            this.sourceProvider.setLoading(false);
        }
    }

    private async ensureMinimumLoadingTime(): Promise<void> {
        if (!this.loadingStartTime) {
            return;
        }

        const elapsedTime = Date.now() - this.loadingStartTime;
        const remainingTime = Math.max(0, this.minimumLoadingTime - elapsedTime);

        if (remainingTime === 0) {
            return;
        }

        await new Promise(resolve => {
            this.loadingTimeoutId = setTimeout(resolve, remainingTime);
        });

        this.cleanupLoadingTimeout();
    }

    private cleanupLoadingTimeout(): void {
        if (this.loadingTimeoutId) {
            clearTimeout(this.loadingTimeoutId);
            this.loadingTimeoutId = null;
        }
    }

    private syncValueWithOptions(): void {
        if (TypeUtils.isStringArray(this.value)) {
            return this.setValue(this.value.filter(v => this.sourceProvider.values.includes(v)));
        }

        if (TypeUtils.isString(this.value) && !this.sourceProvider.values.includes(this.value)) {
            this.setValue(null);
        }

        if (
            this.id === InputIds.OtherConditions.ReasonForClause &&
            this.required &&
            !this.isReadOnly &&
            !this.hasValue &&
            this.sourceProvider.values.length === 1
        ) {
            this.setValue(this.sourceProvider.values[0]);
        }
    }

    private createSourceProvider(source?: SourceModelBase) {
        runInAction(() => {
            if (source) {
                this.sourceProvider = new SourceProvider(source);
                return;
            }

            console.warn(`Source not found for input '${this.id} ${this.guid}'`);
            this.sourceProvider = new SourceProvider(new StaticSourceModel({ id: '', type: '', link: null, data: [] }));
        });
    }
}
